"use client";
import { ChangeEvent, FC, HTMLProps, forwardRef } from "react";

// libs
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

// components
import { Error, ShowWhen } from "@/components/common";

// assets
import { InfoIcon, SearchIcon } from "@/components/assets/icons";

type SearchInputProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: string | boolean;
  value?: string | number;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  currency?: string;
  iconPosition?: "left" | "right";
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
} & HTMLProps<HTMLInputElement>;

const SearchInput: FC<SearchInputProps> = forwardRef<
  HTMLInputElement,
  SearchInputProps
>(
  (
    {
      id,
      label = "",
      value,
      name,
      onChange,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      currency,
      iconPosition = "right",
      ...props
    },
    ref,
  ) => {
    const Icon = icon;

    const inputClasses = classNames(
      "w-full",
      "h-[38px]",
      "bg-transparent",
      "pr-8",
      "border-none",
      "leading-tight",
      "text-base",
      "font-normal",
      "break-all",
      "focus:outline-none",
      className,
    );

    return (
      <div className={classNames("relative w-full", containerClassName)}>
        <div
          className={classNames(
            "input-wrapper flex w-full overflow-hidden rounded-[2.25rem] border border-[2px] bg-transparent p-[8px]",
            {
              // error
              "border-red-600": !!error,
              // active
              "active:border-[#cfe0e2]": !error,
              // disabled
              "disabled:border-none disabled:bg-zinc-100 disabled:text-zinc-400":
                !error,
              // success
              "border-emerald-600": success,
              "border-[#F9F9F9]": !!error || success,
            },
            wrapperClassName,
          )}
        >
          <div
            className={classNames(
              "mr-[10px] flex h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[50%] bg-[#F9F9F9] p-[11px]",
            )}
          >
            <SearchIcon />
          </div>
          <input
            id={id}
            ref={ref}
            className={classNames("input-field pt-[8px]", inputClasses)}
            name={name}
            disabled={disabled}
            maxLength={maxLength}
            type="search"
            placeholder=""
            onChange={(e) => onChange(e)}
            value={value || ""}
            {...props}
          />
          {(icon || !!infoContent) && (
            <div
              className={classNames("absolute !top-[calc(50%-12px)]", {
                "left-[25px]": iconPosition === "left",
                "right-[25px]": iconPosition === "right",
                "input-icon-error": infoContent && error,
              })}
            >
              {icon && <Icon />}
              <ShowWhen condition={infoContent}>
                {infoContent && (
                  <>
                    <InfoIcon data-tooltip-id="my-tooltip" />
                    <Tooltip
                      id="my-tooltip"
                      place="right"
                      className="!left-[67px] !rounded-[14px] !bg-white !p-[3px] drop-shadow"
                    >
                      {infoContent}
                    </Tooltip>
                  </>
                )}
              </ShowWhen>
            </div>
          )}
          <label
            className={classNames(
              "input-label !left-[64px] leading-tight",
              labelClassName,
              !!error && "text-red-600 !opacity-100",
            )}
            htmlFor={id}
          >
            {label}
          </label>
        </div>
        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";

export { SearchInput };
